import React, { useState, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Autocomplete, useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import PageHeader from "../../components/ui/PageHeader";
import { useCollection } from "../../hooks/useCollection";
import EmptyState from "../../components/ui/EmptyState";
import LoadingSpinner from "../../components/ui/LoadingSpinner";

const GeneralLedgerPage = () => {
  const theme = useTheme();
  const { data: journalEntries, loading: loadingEntries } = useCollection("journalEntries");
  const { data: accounts } = useCollection("chartOfAccounts");

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const accountLines = useMemo(() => {
    if (!journalEntries || !selectedAccount) return [];
    
    const lines = [];
    journalEntries
      .filter((entry) => entry.isActive !== false)
      .forEach((entry) => {
        if (entry.lines) {
          entry.lines
            .filter((line) => line.accountCode === selectedAccount.code)
            .forEach((line) => {
              lines.push({
                ...line,
                entryDate: entry.date,
                entryConcept: entry.concept,
                entryNumber: entry.entryNumber,
                entryId: entry.id,
              });
            });
        }
      });

    // Filtrar por fechas
    let filtered = lines;
    if (startDate) {
      filtered = filtered.filter((l) => l.entryDate >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter((l) => l.entryDate <= endDate);
    }

    // Ordenar por fecha
    filtered.sort((a, b) => new Date(a.entryDate) - new Date(b.entryDate));

    return filtered;
  }, [journalEntries, selectedAccount, startDate, endDate]);

  const totals = useMemo(() => {
    return accountLines.reduce(
      (acc, line) => ({
        debit: acc.debit + (line.debit || 0),
        credit: acc.credit + (line.credit || 0),
      }),
      { debit: 0, credit: 0 }
    );
  }, [accountLines]);

  const balance = totals.debit - totals.credit;

  if (loadingEntries) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader
        title="Libro Mayor"
        subtitle={`Consulta de movimientos por cuenta contable${selectedAccount ? ` - ${selectedAccount.code} ${selectedAccount.name}` : ''}`}
      />

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Autocomplete
            options={accounts?.filter(a => a.isActive !== false) || []}
            getOptionLabel={(option) => `${option.code} - ${option.name}`}
            value={selectedAccount}
            onChange={(_, value) => setSelectedAccount(value)}
            renderInput={(params) => (
              <TextField {...params} label="Seleccionar cuenta" size="small" sx={{ minWidth: 300 }} />
            )}
            sx={{ flex: 2, minWidth: 250 }}
          />
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 150 }}
          />
        </Box>
      </Paper>

      {!selectedAccount ? (
        <EmptyState title="Selecciona una cuenta" description="Elige una cuenta del plan de cuentas para ver su libro mayor" />
      ) : accountLines.length === 0 ? (
        <EmptyState title="Sin movimientos" description={`La cuenta ${selectedAccount.code} no tiene movimientos en el período seleccionado`} />
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Asiento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accountLines.map((line, index) => {
                  const runningBalance = accountLines
                    .slice(0, index + 1)
                    .reduce((acc, l) => acc + (l.debit || 0) - (l.credit || 0), 0);
                  return (
                    <TableRow key={line.id || index} hover>
                      <TableCell>{new Date(line.entryDate).toLocaleDateString('es-ES')}</TableCell>
                      <TableCell>{line.entryNumber || '-'}</TableCell>
                      <TableCell>{line.entryConcept}</TableCell>
                      <TableCell>{line.description}</TableCell>
                      <TableCell align="right" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                        {line.debit > 0 ? line.debit.toFixed(2) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
                        {line.credit > 0 ? line.credit.toFixed(2) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: runningBalance >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                        {runningBalance.toFixed(2)}€
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, display: "flex", justifyContent: "flex-end", gap: 4 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Debe</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">{totals.debit.toFixed(2)}€</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Haber</Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">{totals.credit.toFixed(2)}€</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Saldo</Typography>
              <Typography variant="h6" fontWeight={700} color={balance >= 0 ? 'success.main' : 'error.main'}>
                {balance.toFixed(2)}€
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default GeneralLedgerPage;